/**
 * Period end — `/finance/period-end`.
 *
 * The three things a company does to shut a period, on one screen because they
 * are done in one sitting: draft the recurring journals that are due, revalue
 * what is held in foreign currency, and — once a year — sweep profit and loss
 * into retained earnings.
 *
 * Each carries a fact the screen states rather than hides:
 *
 * Materializing is idempotent per period. A second run reports that the draft
 * already existed instead of drafting it twice, and the screen says which of
 * the two happened rather than claiming success either way.
 *
 * Revaluing again does not overwrite. A re-run reverses run N and writes run
 * N+1, so what the company believed at close stays readable; superseded runs
 * are listed as reversed rather than removed.
 *
 * A revaluation line is signed the way a journal line is signed — positive is
 * a debit — which is why one rule, "positive delta is a gain", is right for
 * receivables, payables and bank balances alike.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Play, Plus, RefreshCw } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { formatCalendarDate, formatDateTime } from "@/lib/format";
import { newIdempotencyKey } from "@/features/shared/ops-api";
import { listFiscalYears, type FiscalYear } from "@/features/ledger/calendar-api";

import {
  executeYearEndClose,
  listFxRevaluations,
  listRecurringJournals,
  materializeRecurringJournals,
  runFxRevaluation,
  type FxRevaluationRun,
  type RecurringJournal,
} from "../period-end-api";
import { RecurringJournalDialog } from "./recurring-journal-dialog";

export interface PeriodEndPageProps {
  canReadRecurring?: boolean;
  canMaterialize?: boolean;
  canWriteRecurring?: boolean;
  canReadFx?: boolean;
  canRunFx?: boolean;
  canCloseYear?: boolean;
}

export function PeriodEndPage({
  canReadRecurring = false,
  canMaterialize = false,
  canWriteRecurring = false,
  canReadFx = false,
  canRunFx = false,
  canCloseYear = false,
}: PeriodEndPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [periodId, setPeriodId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [editing, setEditing] = useState<RecurringJournal | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const years = useQuery<FiscalYear[]>({
    queryKey: ["ledger/fiscal-years"],
    queryFn: ({ signal }) => listFiscalYears(signal),
  });
  const periods = useMemo(
    () =>
      (years.data ?? []).flatMap((year) =>
        year.periods.map((period) => ({
          id: period.id,
          label: `${year.code} · ${period.name}`,
          status: period.status,
        })),
      ),
    [years.data],
  );

  const templates = useQuery({
    queryKey: ["finance/recurring-journals"],
    queryFn: async ({ signal }) => [...(await listRecurringJournals(signal)).items],
    enabled: canReadRecurring,
  });

  const runs = useQuery({
    queryKey: ["finance/fx-revaluations"],
    queryFn: async ({ signal }) => [...(await listFxRevaluations(signal)).items],
    enabled: canReadFx,
  });

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };
  const stamp = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const materialize = () => {
    if (periodId === "") return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    void materializeRecurringJournals({ fiscal_period_id: periodId }, newIdempotencyKey())
      .then(async (result) => {
        const created = result.items.filter((item) => item.created).length;
        const existing = result.items.length - created;
        // "Already drafted" is the honest answer to a second run, not a failure
        // and not a success worth celebrating.
        setOutcome(
          t("finance.periodEnd.materialized", "{{created}} drafted, {{existing}} already there.", {
            created,
            existing,
          }),
        );
        await queryClient.invalidateQueries({ queryKey: ["finance/recurring-journals"] });
      })
      .catch(() =>
        setError(t("finance.periodEnd.materializeError", "The drafts could not be created.")),
      )
      .finally(() => setBusy(false));
  };

  const revalue = () => {
    if (periodId === "") return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    void runFxRevaluation(periodId, newIdempotencyKey())
      .then(async (run) => {
        setOutcome(
          t("finance.periodEnd.revalueDone", "Run {{no}} recorded {{count}} position(s).", {
            no: run.run_no,
            count: run.line_count,
          }),
        );
        setOpenRunId(run.id);
        await queryClient.invalidateQueries({ queryKey: ["finance/fx-revaluations"] });
      })
      .catch(() =>
        setError(
          t(
            "finance.periodEnd.revalueError",
            "The revaluation could not run. It needs a closing rate for every open currency.",
          ),
        ),
      )
      .finally(() => setBusy(false));
  };

  const closeYear = (fiscalYearId: string) => {
    setBusy(true);
    setError(null);
    setOutcome(null);
    void executeYearEndClose(fiscalYearId, newIdempotencyKey())
      .then((close) =>
        setOutcome(
          t("finance.periodEnd.yearClosed", "Swept {{count}} account(s) into retained earnings.", {
            count: close.account_count,
          }),
        ),
      )
      .catch(() =>
        setError(t("finance.periodEnd.yearCloseError", "The year-end close could not run.")),
      )
      .finally(() => setBusy(false));
  };

  const openRun: FxRevaluationRun | undefined = (runs.data ?? []).find(
    (run) => run.id === openRunId,
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("finance.periodEnd.title", "Period end")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "finance.periodEnd.subtitle",
            "Draft what recurs, revalue what is held in another currency, and close the year.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="period-end-period">{t("finance.periodEnd.period", "Period")}</Label>
          <Select value={periodId} onValueChange={setPeriodId}>
            <SelectTrigger id="period-end-period" className="w-72">
              <SelectValue placeholder={t("finance.periodEnd.pickPeriod", "Pick a period…")} />
            </SelectTrigger>
            <SelectContent>
              {periods.map((period) => (
                <SelectItem key={period.id} value={period.id}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {outcome === null ? null : (
        <p role="status" className="text-sm text-muted-foreground">
          {outcome}
        </p>
      )}

      {canReadRecurring ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.periodEnd.recurringTitle", "Recurring journals")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.periodEnd.recurringHint",
                "Templates draft an entry on their day each month. Drafting twice is not possible: a second run reports what was already there.",
              )}
            </p>
            {templates.isLoading ? (
              <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
            ) : (templates.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("finance.periodEnd.noTemplates", "No recurring template is defined.")}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[40rem] text-sm">
                  <thead>
                    <tr className="border-b text-xs uppercase text-muted-foreground">
                      <th scope="col" className="p-2 text-start">
                        {t("finance.periodEnd.template", "Template")}
                      </th>
                      <th scope="col" className="p-2 text-start">
                        {t("finance.periodEnd.schedule", "Runs on")}
                      </th>
                      <th scope="col" className="p-2 text-start">
                        {t("finance.periodEnd.window", "Window")}
                      </th>
                      <th scope="col" className="p-2 text-start">
                        {t("finance.periodEnd.reversal", "Auto-reverse")}
                      </th>
                      {canWriteRecurring ? <th scope="col" className="p-2" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {(templates.data ?? []).map((template) => (
                      <tr key={template.id} className="border-b last:border-0">
                        <td className="p-2">
                          <span className="block">
                            {template.name}
                            {template.active ? null : (
                              <Badge variant="outline" className="ms-2">
                                {t("finance.periodEnd.inactive", "Inactive")}
                              </Badge>
                            )}
                          </span>
                          <span dir="ltr" className="block font-mono text-xs text-muted-foreground">
                            {template.code} · {template.currency}
                          </span>
                        </td>
                        <td className="p-2">
                          {t("finance.periodEnd.dayOfMonth", "day {{day}}", {
                            day: template.day_of_month,
                          })}
                        </td>
                        <td className="p-2 text-muted-foreground">
                          {day(template.starts_on)}
                          {template.ends_on === null ? "" : ` – ${day(template.ends_on)}`}
                        </td>
                        <td className="p-2">
                          {template.auto_reverse ? (
                            <Badge variant="secondary">
                              {t("finance.periodEnd.reverses", "Reverses next period")}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        {canWriteRecurring ? (
                          <td className="p-2 text-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditing(template);
                                setEditorOpen(true);
                              }}
                            >
                              {t("actions.edit", "Edit")}
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {canMaterialize ? (
                <Button type="button" disabled={periodId === "" || busy} onClick={materialize}>
                  <CalendarClock className="size-4" aria-hidden="true" />
                  {t("finance.periodEnd.materialize", "Draft what is due")}
                </Button>
              ) : null}
              {canWriteRecurring ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditing(null);
                    setEditorOpen(true);
                  }}
                >
                  <Plus className="size-4" aria-hidden="true" />
                  {t("finance.periodEnd.newTemplate", "New recurring template")}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {canReadFx ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.periodEnd.fxTitle", "Foreign-currency revaluation")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.periodEnd.fxHint",
                "Restates open receivables, payables and bank balances at the closing rate. Running again reverses the previous run and writes a new one; nothing is overwritten.",
              )}
            </p>
            {runs.isLoading ? (
              <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
            ) : (runs.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("finance.periodEnd.noRuns", "No revaluation has been run yet.")}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {(runs.data ?? []).map((run) => (
                  <li key={run.id}>
                    <button
                      type="button"
                      onClick={() => setOpenRunId(run.id === openRunId ? null : run.id)}
                      className="flex w-full flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-start text-sm hover:bg-muted/50"
                    >
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge variant={run.status === "completed" ? "success" : "outline"}>
                          {t(`finance.fxRunStatus.${run.status}`, run.status)}
                        </Badge>
                        <span>
                          {t("finance.periodEnd.runNo", "Run {{no}}", { no: run.run_no })} ·{" "}
                          {day(run.rate_date)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {t("finance.periodEnd.lineCount", "{{count}} position(s)", {
                            count: run.line_count,
                          })}
                        </span>
                      </span>
                      <span className="flex flex-wrap items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {stamp(run.executed_at)}
                        </span>
                        <MoneyDisplay value={run.net_adjustment} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {openRun === undefined ? null : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full min-w-[44rem] text-sm">
                  <thead>
                    <tr className="border-b text-xs uppercase text-muted-foreground">
                      <th scope="col" className="p-2 text-start">
                        {t("finance.periodEnd.scope", "Position")}
                      </th>
                      <th scope="col" className="p-2 text-end">
                        {t("finance.periodEnd.foreign", "Held")}
                      </th>
                      <th scope="col" className="p-2 text-end">
                        {t("finance.periodEnd.rate", "Closing rate")}
                      </th>
                      <th scope="col" className="p-2 text-end">
                        {t("finance.periodEnd.booked", "Booked")}
                      </th>
                      <th scope="col" className="p-2 text-end">
                        {t("finance.periodEnd.revalued", "Revalued")}
                      </th>
                      <th scope="col" className="p-2 text-end">
                        {t("finance.periodEnd.delta", "Gain / loss")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {openRun.lines.map((line) => (
                      <tr key={line.id} className="border-b last:border-0">
                        <td className="p-2">{t(`finance.fxScope.${line.scope}`, line.scope)}</td>
                        <td className="p-2 text-end">
                          <MoneyDisplay value={line.foreign_amount} display="code" />
                        </td>
                        <td className="p-2 text-end tabular-nums" dir="ltr">
                          {line.closing_rate}
                        </td>
                        <td className="p-2 text-end">
                          <MoneyDisplay value={line.booked_base_amount} display="none" />
                        </td>
                        <td className="p-2 text-end">
                          <MoneyDisplay value={line.revalued_base_amount} display="none" />
                        </td>
                        <td className="p-2 text-end">
                          <MoneyDisplay value={line.delta} display="none" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {canRunFx ? (
              <Button
                type="button"
                variant="outline"
                disabled={periodId === "" || busy}
                onClick={revalue}
              >
                <RefreshCw className="size-4" aria-hidden="true" />
                {t("finance.periodEnd.revalue", "Revalue this period")}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {canCloseYear ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.periodEnd.yearTitle", "Year-end close")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.periodEnd.yearHint",
                "Sweeps the year's income and expense balances into retained earnings. The server chooses the closing period and the equity account.",
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {(years.data ?? []).map((year) => (
                <Button
                  key={year.id}
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => closeYear(year.id)}
                >
                  <Play className="size-4" aria-hidden="true" />
                  {t("finance.periodEnd.closeYear", "Close {{code}}", { code: year.code })}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {canWriteRecurring ? (
        <RecurringJournalDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          template={editing}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ["finance/recurring-journals"] });
          }}
        />
      ) : null}
    </div>
  );
}
